import { useEffect, useState, type KeyboardEvent } from 'react';
import { Save } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { ApiRequestError } from '@/lib/apiClient';
import type { House } from '@/types/house';
import { housesApi } from '../api/houses.api';
import { EMPTY_HOUSE, houseSchema, type HouseFormValues } from '../schemas/house.schema';

export interface HouseFormModalProps {
  /** The house being renamed, or null to add one. */
  house: House | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (house: House) => void;
}

type FieldErrors = Partial<Record<keyof HouseFormValues, string>>;

/** Adding a house, or renaming it. A name already in use lands on the name field. */
export function HouseFormModal({ house, isOpen, onClose, onSaved }: HouseFormModalProps) {
  const [values, setValues] = useState<HouseFormValues>(EMPTY_HOUSE);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Refilled each time it opens, so the last house never shows under the next.
  useEffect(() => {
    if (!isOpen) return;
    setValues(house ? { name: house.name, description: house.description ?? '' } : EMPTY_HOUSE);
    setFieldErrors({});
    setFormError(null);
  }, [house, isOpen]);

  function set<K extends keyof HouseFormValues>(key: K, value: HouseFormValues[K]) {
    setValues((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => ({ ...current, [key]: undefined }));
  }

  async function handleSubmit() {
    if (isSaving) return;
    const parsed = houseSchema.safeParse(values);

    if (!parsed.success) {
      const errors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof HouseFormValues | undefined;
        if (field && !errors[field]) errors[field] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setIsSaving(true);

    try {
      const payload = { name: parsed.data.name, description: parsed.data.description };
      const saved = house
        ? await housesApi.update(house.uuid, payload)
        : await housesApi.create(payload);
      onSaved(saved);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.code === 'HOUSE_NAME_TAKEN') {
        setFieldErrors({ name: caught.fieldMap.name ?? caught.message });
        return;
      }
      setFormError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  // Not a <form>, like the subject form: Enter on the name saves, Enter in the
  // description still starts a new line.
  function submitOnEnter(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();
    void handleSubmit();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={house ? `Edit ${house.name}` : 'Add a house'}
      description="Houses are school-wide — students from every class and section can be placed in them."
      size="sm"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            isLoading={isSaving}
            leftIcon={<Save className="h-4 w-4" />}
          >
            {house ? 'Save changes' : 'Add house'}
          </Button>
        </>
      }
    >
      <div onKeyDown={submitOnEnter} className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title="Could not save this house">
            {formError}
          </Alert>
        )}

        <Input
          label="House name"
          placeholder="e.g. Lotus"
          autoComplete="off"
          maxLength={40}
          value={values.name}
          error={fieldErrors.name}
          hint={
            house && house.studentCount > 0
              ? 'Renaming it renames it for every student in it.'
              : undefined
          }
          onChange={(event) => set('name', event.target.value)}
        />

        <Textarea
          label="Motto or description (optional)"
          placeholder="e.g. Strength through unity"
          rows={3}
          maxLength={200}
          value={values.description}
          error={fieldErrors.description}
          onChange={(event) => set('description', event.target.value)}
        />
      </div>
    </Modal>
  );
}
